import React, { useState, useMemo, useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Crown, ExternalLink, Trash2, Gamepad2, Download, Medal, Gift, Key, DollarSign, CheckCircle, X, Check, FileSpreadsheet, FileJson, Share2, Twitter, Facebook, Send, Eye, EyeOff, Copy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WinnersTab = ({ winners, token, onRefresh }) => {
  const [deletingId, setDeletingId] = useState(null);
  const [publishingChallenge, setPublishingChallenge] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedWinnerForShare, setSelectedWinnerForShare] = useState(null);
  const [pushingWinnerId, setPushingWinnerId] = useState(null);

  const handlePushWinner = async (winnerId) => {
    setPushingWinnerId(winnerId);
    try {
      const res = await axios.post(`${API}/admin/winners/${winnerId}/push-notification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Winner announcement pushed to Discord!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push winner notification');
    } finally {
      setPushingWinnerId(null);
    }
  };

  // Group winners by challenge
  const groupedWinners = useMemo(() => {
    const groups = {};
    const safeWinners = Array.isArray(winners) ? winners : [];
    safeWinners.forEach(w => {
      const challengeId = w.challenge_id || 'unknown';
      if (!groups[challengeId]) {
        groups[challengeId] = {
          challenge_id: challengeId,
          challenge_title: w.challenge_title || 'Unknown Challenge',
          game_name: w.game_name,
          game_cover_url: w.game_cover_url,
          winners: [],
          hasUnpublished: false,
          allPublished: true,
          latestDate: null
        };
      }
      groups[challengeId].winners.push(w);
      if (!w.published) {
        groups[challengeId].hasUnpublished = true;
        groups[challengeId].allPublished = false;
      }
      // Track the latest announced_at date for sorting
      const winnerDate = w.announced_at ? new Date(w.announced_at) : null;
      if (winnerDate && (!groups[challengeId].latestDate || winnerDate > groups[challengeId].latestDate)) {
        groups[challengeId].latestDate = winnerDate;
      }
    });
    // Sort winners within each group by latest first (announced_at descending), then by place
    Object.values(groups).forEach(group => {
      group.winners.sort((a, b) => {
        // First sort by announced_at (latest first)
        const dateA = a.announced_at ? new Date(a.announced_at) : new Date(0);
        const dateB = b.announced_at ? new Date(b.announced_at) : new Date(0);
        if (dateB - dateA !== 0) return dateB - dateA;
        // Then by place
        return (a.place || 1) - (b.place || 1);
      });
    });
    // Sort challenges by latest date (newest first)
    return Object.values(groups).sort((a, b) => {
      if (!a.latestDate && !b.latestDate) return 0;
      if (!a.latestDate) return 1;
      if (!b.latestDate) return -1;
      return b.latestDate - a.latestDate;
    });
  }, [winners]);

  // Stats
  const stats = useMemo(() => {
    const safeWinners = Array.isArray(winners) ? winners : [];
    const published = safeWinners.filter(w => w.published).length;
    const draft = safeWinners.filter(w => !w.published).length;
    const firstPlace = safeWinners.filter(w => w.place === 1 || !w.place).length;
    const secondPlace = safeWinners.filter(w => w.place === 2).length;
    const thirdPlace = safeWinners.filter(w => w.place === 3).length;
    return { published, draft, firstPlace, secondPlace, thirdPlace };
  }, [winners]);

  const handleDeleteWinner = async (winnerId) => {
    if (!window.confirm('Are you sure you want to remove this winner?')) return;
    
    setDeletingId(winnerId);
    try {
      await axios.delete(`${API}/admin/winners/${winnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Winner removed');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete winner');
    } finally {
      setDeletingId(null);
    }
  };

  const handlePublishWinners = async (challengeId, challengeTitle) => {
    if (!window.confirm(`Publish all winners for "${challengeTitle}"? They will appear on the public Hall of Winners and Leaderboard.`)) return;
    
    setPublishingChallenge(challengeId);
    try {
      const response = await axios.post(`${API}/admin/challenges/${challengeId}/publish-winners`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || 'Winners published!');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish winners');
    } finally {
      setPublishingChallenge(null);
    }
  };

  const openShareModal = (winner) => {
    setSelectedWinnerForShare(winner);
    setShowShareModal(true);
  };

  const getPlaceBadge = (place) => {
    switch(place) {
      case 1: return { emoji: '🥇', text: '1st', bg: 'bg-yellow-500/20', color: 'text-yellow-400', border: 'border-yellow-500' };
      case 2: return { emoji: '🥈', text: '2nd', bg: 'bg-gray-500/20', color: 'text-gray-300', border: 'border-gray-400' };
      case 3: return { emoji: '🥉', text: '3rd', bg: 'bg-amber-500/20', color: 'text-amber-500', border: 'border-amber-500' };
      default: return { emoji: '🏆', text: '1st', bg: 'bg-yellow-500/20', color: 'text-yellow-400', border: 'border-yellow-500' };
    }
  };

  const getPrizeDisplay = (winner) => {
    if (winner.prize_info?.prize_type === 'money' || !winner.prize_info) {
      return <span className="text-green-400 font-bold">${winner.prize_info?.amount || winner.prize_amount || 0}</span>;
    } else if (winner.prize_info?.prize_type === 'game_code') {
      return <span className="text-blue-400 font-bold">🔑 Game Code</span>;
    } else if (winner.prize_info?.prize_type === 'item') {
      return <span className="text-purple-400 font-bold">🎁 Item</span>;
    }
    return <span className="text-green-400 font-bold">${winner.prize_amount || 0}</span>;
  };

  return (
    <>
      <Card className="glass-card border-yellow-500/30">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              <Crown className="w-5 h-5 text-yellow-400" />
              Winners ({winners.length})
            </CardTitle>
            
            {/* Stats */}
            <div className="flex items-center gap-2 text-xs">
              {stats.draft > 0 && (
                <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded font-bold flex items-center gap-1">
                  <EyeOff className="w-3 h-3" /> {stats.draft} Draft
                </span>
              )}
              {stats.published > 0 && (
                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded font-bold flex items-center gap-1">
                  <Eye className="w-3 h-3" /> {stats.published} Published
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs border-purple-500/50 hover:bg-purple-500/20"
                onClick={() => setShowExportModal(true)}
              >
                <Download className="w-3 h-3 mr-1" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {winners.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Crown className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No winners yet</p>
              <p className="text-sm">Pick winners from the Submissions tab</p>
            </div>
          ) : (
            /* Grouped by Challenge */
            <div className="space-y-4">
              {groupedWinners.map((group) => (
                <div key={group.challenge_id} className="border border-purple-500/30 rounded-xl overflow-hidden">
                  {/* Challenge Header */}
                  <div className="p-3 bg-gradient-to-r from-purple-500/10 via-transparent to-blue-500/10 border-b border-purple-500/20">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        {group.game_cover_url && (
                          <img 
                            src={group.game_cover_url} 
                            alt={group.game_name}
                            className="w-10 h-14 object-cover rounded border border-purple-500/30"
                          />
                        )}
                        <div>
                          <h3 className="font-bold text-white text-sm" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                            {group.challenge_title}
                          </h3>
                          {group.game_name && (
                            <div className="flex items-center gap-1 text-xs text-purple-400">
                              <Gamepad2 className="w-3 h-3" />
                              {group.game_name}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500">{group.winners.length} winner{group.winners.length > 1 ? 's' : ''}</span>
                            {group.allPublished ? (
                              <span className="text-xs text-green-400 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Published
                              </span>
                            ) : (
                              <span className="text-xs text-orange-400 flex items-center gap-1">
                                <EyeOff className="w-3 h-3" /> Draft
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Post Winners Button */}
                      {group.hasUnpublished && (
                        <Button
                          size="sm"
                          className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold"
                          onClick={() => handlePublishWinners(group.challenge_id, group.challenge_title)}
                          disabled={publishingChallenge === group.challenge_id}
                        >
                          {publishingChallenge === group.challenge_id ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                              Publishing...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Post Winners
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Winners List */}
                  <div className="p-2 space-y-2">
                    {group.winners.map((winner) => {
                      const placeBadge = getPlaceBadge(winner.place);
                      
                      return (
                        <div 
                          key={winner.id} 
                          className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 rounded-lg bg-black/30 border ${winner.published ? 'border-green-500/30' : 'border-orange-500/30'}`}
                        >
                          {/* Place Badge + Profile Row */}
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full ${placeBadge.bg} ${placeBadge.color} flex flex-col items-center justify-center border ${placeBadge.border} flex-shrink-0`}>
                              <span className="text-sm">{placeBadge.emoji}</span>
                              <span className="text-[8px] font-bold">{placeBadge.text}</span>
                            </div>
                            
                            <img 
                              src={winner.profile_image_url || 'https://via.placeholder.com/40'} 
                              alt={winner.display_name}
                              className="w-10 h-10 rounded-full border border-gray-600 flex-shrink-0"
                            />
                          
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`font-bold ${placeBadge.color} truncate`}>{winner.display_name}</span>
                                {!winner.published && (
                                  <span className="text-[10px] px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded">DRAFT</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">@{winner.twitch_username}</div>
                              <div className="text-sm mt-0.5">{getPrizeDisplay(winner)}</div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 ml-auto sm:ml-0 sm:flex-shrink-0">
                            <a 
                              href={winner.video_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="p-2 rounded hover:bg-white/10 text-red-400"
                              title="Watch"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                            <button
                              onClick={() => handlePushWinner(winner.id)}
                              disabled={pushingWinnerId === winner.id}
                              className="p-2 rounded hover:bg-indigo-500/20 text-indigo-400 disabled:opacity-50"
                              title="Push Winner to Discord"
                            >
                              {pushingWinnerId === winner.id ? (
                                <div className="w-4 h-4 border-2 border-indigo-400/30 border-t-indigo-400 rounded-full animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </button>
                            {winner.published && (
                              <button
                                onClick={() => openShareModal(winner)}
                                className="p-2 rounded hover:bg-purple-500/20 text-purple-400"
                                title="Share"
                              >
                                <Share2 className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteWinner(winner.id)}
                              disabled={deletingId === winner.id}
                              className="p-2 rounded hover:bg-red-500/20 text-red-400 disabled:opacity-50"
                              title="Delete"
                            >
                              {deletingId === winner.id ? (
                                <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal 
          winners={winners} 
          challenges={groupedWinners}
          token={token}
          onClose={() => setShowExportModal(false)} 
        />
      )}

      {/* Share Modal */}
      {showShareModal && selectedWinnerForShare && (
        <ShareModal
          winner={selectedWinnerForShare}
          onClose={() => {
            setShowShareModal(false);
            setSelectedWinnerForShare(null);
          }}
        />
      )}
    </>
  );
};

// Export Modal Component
const ExportModal = ({ winners, challenges, token, onClose }) => {
  const [exportFormat, setExportFormat] = useState('csv');
  const [selectedChallenges, setSelectedChallenges] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  const toggleChallenge = (challengeId) => {
    const newSet = new Set(selectedChallenges);
    if (newSet.has(challengeId)) {
      newSet.delete(challengeId);
    } else {
      newSet.add(challengeId);
    }
    setSelectedChallenges(newSet);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Filter winners by selected challenges (or all if none selected)
      let winnersToExport = winners;
      if (selectedChallenges.size > 0) {
        winnersToExport = winners.filter(w => selectedChallenges.has(w.challenge_id));
      }

      if (exportFormat === 'json') {
        const blob = new Blob([JSON.stringify(winnersToExport, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `winners_${new Date().toISOString().split('T')[0]}.json`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        // CSV export
        const headers = ['Place', 'Display Name', 'Username', 'Prize', 'Challenge', 'Game', 'Published', 'Date'];
        const rows = winnersToExport.map(w => [
          w.place || 1,
          w.display_name,
          w.twitch_username,
          w.prize_info?.amount || w.prize_amount || 0,
          w.challenge_title || '',
          w.game_name || '',
          w.published ? 'Yes' : 'No',
          new Date(w.announced_at).toLocaleDateString()
        ]);
        
        const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `winners_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      }
      
      toast.success('Export successful!');
      onClose();
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative bg-gray-900 border border-purple-500/30 rounded-xl p-4 w-full max-w-md">
        <h3 className="text-lg font-bold text-white mb-4">Export Winners</h3>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 block mb-2">Format</label>
            <div className="flex gap-2">
              <button
                onClick={() => setExportFormat('csv')}
                className={`flex-1 p-2 rounded-lg border ${exportFormat === 'csv' ? 'border-green-500 bg-green-500/20' : 'border-gray-600'}`}
              >
                <FileSpreadsheet className="w-4 h-4 mx-auto mb-1 text-green-400" />
                <span className="text-xs">CSV</span>
              </button>
              <button
                onClick={() => setExportFormat('json')}
                className={`flex-1 p-2 rounded-lg border ${exportFormat === 'json' ? 'border-blue-500 bg-blue-500/20' : 'border-gray-600'}`}
              >
                <FileJson className="w-4 h-4 mx-auto mb-1 text-blue-400" />
                <span className="text-xs">JSON</span>
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 block mb-2">Filter by Challenge (optional)</label>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {challenges.map(c => (
                <label key={c.challenge_id} className="flex items-center gap-2 p-2 rounded hover:bg-white/5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedChallenges.has(c.challenge_id)}
                    onChange={() => toggleChallenge(c.challenge_id)}
                    className="rounded"
                  />
                  <span className="text-sm text-white">{c.challenge_title}</span>
                  <span className="text-xs text-gray-500">({c.winners.length})</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button 
            className="flex-1 bg-purple-600 hover:bg-purple-500"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Share Modal Component - Direct Image URL for Twitter
const ShareModal = ({ winner, onClose }) => {
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [sharePageUrl, setSharePageUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const placeBadge = {
    1: { emoji: '🥇', text: '1ST PLACE', gradient: 'from-yellow-500 to-amber-600' },
    2: { emoji: '🥈', text: '2ND PLACE', gradient: 'from-gray-400 to-gray-500' },
    3: { emoji: '🥉', text: '3RD PLACE', gradient: 'from-amber-600 to-amber-700' }
  }[winner.place || 1];

  const getPrizeText = () => {
    if (winner.prize_info?.prize_type === 'money' || !winner.prize_info) {
      return `$${winner.prize_info?.amount || winner.prize_amount || 0}`;
    } else if (winner.prize_info?.prize_type === 'game_code') {
      return winner.prize_info?.description || 'Game Code Prize';
    } else if (winner.prize_info?.prize_type === 'item') {
      return winner.prize_info?.description || 'Prize Item';
    }
    return 'Winner';
  };

  // Generate image and upload to get direct image URL
  const generateImage = async () => {
    if (imageUrl) return { imageUrl, sharePageUrl };
    
    setGenerating(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#0a0a0a',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      
      const imageData = canvas.toDataURL('image/jpeg', 0.92);
      
      const response = await axios.post(`${API}/share-card/upload`, {
        image_data: imageData,
        winner_id: winner.id,
        winner_name: winner.display_name,
        challenge_title: winner.challenge_title || 'Challenge',
        place: winner.place || 1,
        prize: getPrizeText()
      });
      
      const shareId = response.data.share_id;
      const directImageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share-card/${shareId}/image`;
      // Use backend URL for Twitter sharing (has server-side meta tags)
      const twitterShareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/${shareId}`;
      
      setImageUrl(directImageUrl);
      setSharePageUrl(twitterShareUrl);
      
      return { imageUrl: directImageUrl, sharePageUrl: twitterShareUrl };
    } catch (error) {
      console.error('Failed to generate image:', error);
      toast.error('Failed to create share image');
      return null;
    } finally {
      setGenerating(false);
    }
  };

  // Share to Twitter - uses backend share page with proper Twitter Card meta tags
  const shareToTwitter = async () => {
    const result = await generateImage();
    if (!result) return;
    
    // Just share the URL - Twitter will pick up image from summary_large_image meta tags
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(result.sharePageUrl)}`, '_blank');
    toast.success('Opening Twitter - image will show in preview!');
  };

  const shareToFacebook = async () => {
    const result = await generateImage();
    if (!result) return;
    
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(result.sharePageUrl)}`, '_blank');
    toast.success('Opening Facebook...');
  };

  const copyImageUrl = async () => {
    const result = await generateImage();
    if (!result) return;
    
    try {
      await navigator.clipboard.writeText(result.imageUrl);
      setCopied(true);
      toast.success('Image URL copied!');
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = result.imageUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      toast.success('Image URL copied!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const copyShareUrl = async () => {
    const result = await generateImage();
    if (!result) return;
    
    try {
      await navigator.clipboard.writeText(result.sharePageUrl);
      toast.success('Share link copied!');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const downloadCard = async () => {
    setGenerating(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#0a0a0a',
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `winner_${winner.display_name}_${Date.now()}.jpg`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('Card downloaded!');
      }, 'image/jpeg', 0.92);
    } catch (error) {
      toast.error('Failed to download');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-xl bg-gradient-to-br from-gray-900 via-gray-900 to-purple-950/30 border border-purple-500/30 rounded-2xl shadow-2xl overflow-hidden max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between p-3 border-b border-purple-500/20">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-purple-400" />
            <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>Share Winner</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-white/10">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Share Card Preview - Polished Design */}
          <div 
            ref={cardRef}
            style={{
              width: '500px',
              height: '262px',
              background: 'linear-gradient(145deg, #0f0f1a 0%, #1a1a2e 40%, #16213e 70%, #0f0f23 100%)',
              borderRadius: '14px',
              overflow: 'hidden',
              position: 'relative',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
          >
            {/* Background Glow */}
            <div style={{
              position: 'absolute',
              top: '-40px',
              right: '-40px',
              width: '150px',
              height: '150px',
              background: 'radial-gradient(circle, rgba(234,179,8,0.2) 0%, transparent 70%)',
              pointerEvents: 'none'
            }} />
            
            {/* Main Content */}
            <div style={{ position: 'relative', padding: '20px', height: '100%' }}>
              
              {/* Row 1: Challenge + Badge */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                
                {/* Left: Game Cover + Challenge Info */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  {winner.game_cover_url && (
                    <img 
                      src={winner.game_cover_url} 
                      alt=""
                      crossOrigin="anonymous"
                      style={{
                        width: '42px',
                        height: '56px',
                        objectFit: 'cover',
                        borderRadius: '6px',
                        border: '2px solid rgba(147,51,234,0.5)',
                        flexShrink: 0
                      }}
                    />
                  )}
                  <div>
                    <div style={{ fontSize: '8px', color: '#a78bfa', letterSpacing: '2px', fontWeight: 700, marginBottom: '2px' }}>CHALLENGE</div>
                    <div style={{ fontSize: '14px', fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
                      {winner.challenge_title || 'Gaming Challenge'}
                    </div>
                    {winner.game_name && (
                      <div style={{ fontSize: '11px', color: '#a78bfa', marginTop: '3px' }}>
                        🎮 {winner.game_name}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Right: Place Badge */}
                <div style={{
                  background: winner.place === 1 ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 
                             winner.place === 2 ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' :
                             'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  flexShrink: 0
                }}>
                  <span style={{ fontSize: '16px' }}>{placeBadge.emoji}</span>
                  <span style={{ fontSize: '12px', fontWeight: 900, color: 'white', letterSpacing: '1px' }}>{placeBadge.text}</span>
                </div>
              </div>
              
              {/* Row 2: Winner Info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                
                {/* Profile Picture Container */}
                <div style={{ position: 'relative', width: '64px', height: '64px', flexShrink: 0 }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    border: '3px solid #eab308',
                    overflow: 'hidden',
                    boxShadow: '0 0 16px rgba(234,179,8,0.3)',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {winner.profile_image_url ? (
                      <img 
                        src={winner.profile_image_url} 
                        alt=""
                        crossOrigin="anonymous"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span style={{ fontSize: '26px', fontWeight: 900, color: 'white' }}>
                        {winner.display_name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    )}
                  </div>
                  {/* Crown Badge */}
                  <div style={{
                    position: 'absolute',
                    bottom: '0px',
                    right: '0px',
                    width: '24px',
                    height: '24px',
                    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #0f0f1a'
                  }}>
                    <span style={{ fontSize: '12px' }}>👑</span>
                  </div>
                </div>
                
                {/* Name + Handle */}
                <div>
                  <div style={{ fontSize: '20px', fontWeight: 900, color: 'white', marginBottom: '2px' }}>
                    {winner.display_name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#a78bfa' }}>@{winner.twitch_username}</div>
                </div>
              </div>

              {/* Row 3: Prize + Branding */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                
                {/* Prize Box */}
                <div style={{
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(34,197,94,0.4)',
                  borderRadius: '10px',
                  padding: '8px 14px'
                }}>
                  <div style={{ fontSize: '8px', color: '#9ca3af', letterSpacing: '1px', fontWeight: 700, marginBottom: '2px' }}>💰 PRIZE</div>
                  <div style={{ fontSize: '18px', fontWeight: 900, color: '#4ade80' }}>
                    {getPrizeText()}
                  </div>
                </div>
                
                {/* Branding */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '11px', color: '#a78bfa', fontWeight: 700 }}>
                    🏆 CesarM Challenges
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Generated URLs */}
          {imageUrl && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 space-y-2">
              <p className="text-xs text-green-400 text-center font-bold">✅ Image ready to share!</p>
              
              <div>
                <label className="text-[10px] text-gray-400 uppercase">Direct Image URL</label>
                <div className="flex items-center gap-2 bg-black/40 rounded-lg p-2 mt-1">
                  <input 
                    type="text" 
                    value={imageUrl} 
                    readOnly 
                    className="flex-1 bg-transparent text-xs text-gray-300 outline-none truncate"
                  />
                  <button 
                    onClick={copyImageUrl}
                    className={`px-2 py-1 rounded text-xs font-bold ${copied ? 'bg-green-500 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'}`}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Main Action: Share to Twitter */}
          <Button
            className="w-full h-12 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 font-bold"
            onClick={shareToTwitter}
            disabled={generating}
          >
            {generating ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Generating image...
              </>
            ) : (
              <>
                <Twitter className="w-5 h-5 mr-2" />
                Share to Twitter
              </>
            )}
          </Button>
          
          <p className="text-xs text-gray-500 text-center">
            Image will appear in your tweet preview!
          </p>
          
          {/* Other Options */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={shareToFacebook}
              disabled={generating}
              className="flex items-center justify-center gap-2 p-3 rounded-xl bg-black/30 border border-gray-700 hover:border-blue-600 disabled:opacity-50"
            >
              <Facebook className="w-5 h-5 text-blue-600" />
              <span className="text-sm">Facebook</span>
            </button>
            <button
              onClick={copyImageUrl}
              disabled={generating}
              className="flex items-center justify-center gap-2 p-3 rounded-xl bg-black/30 border border-gray-700 hover:border-green-500 disabled:opacity-50"
            >
              <Copy className="w-5 h-5 text-green-500" />
              <span className="text-sm">Copy URL</span>
            </button>
          </div>

          {/* Download Button */}
          <button
            onClick={downloadCard}
            disabled={generating}
            className="w-full flex items-center justify-center gap-2 p-2 rounded-xl bg-black/30 border border-gray-700 hover:border-purple-500 disabled:opacity-50"
          >
            <Download className="w-4 h-4 text-purple-500" />
            <span className="text-sm">Download Card</span>
          </button>

          <Button variant="outline" className="w-full" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default WinnersTab;
